/**
 * Alta/edición de un reporte maestro (incluye deadline).
 */
import React, { useEffect, useState } from 'react';
import { datetimeLocalToIso, toDatetimeLocalValue } from '../../datetimeLocal';
import { studio, FULL_ROW } from './studioUi';

const ReportForm = ({ nuevoMaestro, setNuevoMaestro, clients, projects, editandoId, onSave, onCancel }) => {
    const [tempDeadline, setTempDeadline] = useState('');

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ];

    useEffect(() => {
        setTempDeadline(toDatetimeLocalValue(nuevoMaestro.approvalDeadline));
    }, [nuevoMaestro.approvalDeadline]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!nuevoMaestro.clientId || !nuevoMaestro.projectId) {
            return alert('Por favor, selecciona un cliente y un proyecto.');
        }

        const selectedClientData = clients.find((c) => c.id === nuevoMaestro.clientId);
        const selectedProjectData = projects.find((p) => p.id === nuevoMaestro.projectId);

        const payload = {
            ...nuevoMaestro,
            cliente: selectedClientData ? selectedClientData.nombre : '',
            proyecto: selectedProjectData ? selectedProjectData.nombre : '',
            hasEditableUrl: !!nuevoMaestro.hasEditableUrl,
            editableUrl: nuevoMaestro.hasEditableUrl ? nuevoMaestro.editableUrl : '',
            corUrl: nuevoMaestro.corUrl || '',
            enfoque: nuevoMaestro.enfoque || 'Interno',
            approvalDeadline: datetimeLocalToIso(tempDeadline),
        };
        if (nuevoMaestro.isCustom) {
            payload.isCustom = true;
            payload.dashboardUrl = nuevoMaestro.dashboardUrl && String(nuevoMaestro.dashboardUrl).startsWith('custom:')
                ? nuevoMaestro.dashboardUrl
                : (editandoId ? `custom:${editandoId}` : 'custom-report-view');
        }

        delete payload.id;
        delete payload.password;
        delete payload.confirmPassword;
        delete payload.customConfig;

        onSave(payload);
    };

    return (
        <div style={studio.formPanel}>
            <h3 style={studio.formTitle}>{editandoId ? 'Editar reporte' : 'Nuevo reporte'}</h3>
            <form onSubmit={handleSubmit} style={studio.formGrid}>
                <label>
                    <span style={studio.fieldLabel}>Cliente</span>
                    <select value={nuevoMaestro.clientId || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, clientId: e.target.value, projectId: '' })} style={studio.control} required>
                        <option value="">Seleccionar</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                </label>
                <label>
                    <span style={studio.fieldLabel}>Proyecto</span>
                    <select value={nuevoMaestro.projectId || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, projectId: e.target.value })} style={studio.control} required disabled={!nuevoMaestro.clientId && !editandoId}>
                        <option value="">Seleccionar</option>
                        {projects.filter((p) => p.clientId === nuevoMaestro.clientId).map((p) => (
                            <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                    </select>
                </label>
                <label style={{ gridColumn: FULL_ROW }}>
                    <span style={studio.fieldLabel}>Nombre</span>
                    <input placeholder="Nombre del reporte" value={nuevoMaestro.reporteNombre} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, reporteNombre: e.target.value })} style={studio.control} required />
                </label>
                <label>
                    <span style={studio.fieldLabel}>Enfoque</span>
                    <select value={nuevoMaestro.enfoque || 'Interno'} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, enfoque: e.target.value })} style={studio.control} required>
                        <option value="Interno">Interno</option>
                        <option value="Cliente">Cliente</option>
                    </select>
                </label>
                {nuevoMaestro.isCustom ? (
                    <div style={studio.helper}>Visualización nativa (constructor). No usa iframe de Looker.</div>
                ) : (
                    <label>
                        <span style={studio.fieldLabel}>URL Looker Studio</span>
                        <input placeholder="https://…" value={nuevoMaestro.dashboardUrl} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, dashboardUrl: e.target.value })} style={studio.control} required />
                    </label>
                )}
                <div style={{ gridColumn: FULL_ROW, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="hasEditableUrl" checked={nuevoMaestro.hasEditableUrl || false} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, hasEditableUrl: e.target.checked })} />
                    <label htmlFor="hasEditableUrl" style={{ fontWeight: 600, fontSize: '0.9rem' }}>Tiene enlace editable</label>
                </div>
                {nuevoMaestro.hasEditableUrl && (
                    <label style={{ gridColumn: FULL_ROW }}>
                        <span style={studio.fieldLabel}>URL editable</span>
                        <input placeholder="URL editable (Looker Studio)" value={nuevoMaestro.editableUrl || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, editableUrl: e.target.value })} style={studio.control} required />
                    </label>
                )}
                <label>
                    <span style={studio.fieldLabel}>Año</span>
                    <select value={nuevoMaestro.versionYear || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, versionYear: e.target.value })} style={studio.control} required>
                        <option value="">Seleccionar</option>
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </label>
                <label>
                    <span style={studio.fieldLabel}>Mes</span>
                    <select value={nuevoMaestro.versionMonth || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, versionMonth: e.target.value })} style={studio.control} required>
                        <option value="">Seleccionar</option>
                        {monthNames.map((name, index) => (
                            <option key={index + 1} value={index + 1}>{name}</option>
                        ))}
                    </select>
                </label>
                <label style={{ gridColumn: FULL_ROW }}>
                    <span style={studio.fieldLabel}>Deadline de aprobación</span>
                    <input type="datetime-local" id="approvalDeadline" value={tempDeadline} onChange={(e) => setTempDeadline(e.target.value)} style={studio.control} />
                </label>
                <label style={{ gridColumn: FULL_ROW }}>
                    <span style={studio.fieldLabel}>Repositorio</span>
                    <input placeholder="URL del repositorio (opcional)" value={nuevoMaestro.repositorio || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, repositorio: e.target.value })} style={studio.control} />
                </label>
                <label style={{ gridColumn: FULL_ROW }}>
                    <span style={studio.fieldLabel}>Tarea COR</span>
                    <input placeholder="URL de la tarea COR (opcional)" value={nuevoMaestro.corUrl || ''} onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, corUrl: e.target.value })} style={studio.control} />
                </label>
                <div style={{ gridColumn: FULL_ROW, display: 'flex', gap: 10 }}>
                    <button type="submit" style={studio.primaryBtn}>{editandoId ? 'Actualizar reporte' : 'Guardar reporte'}</button>
                    <button type="button" onClick={onCancel} style={studio.ghostBtn}>Cancelar</button>
                </div>
            </form>
        </div>
    );
};

export default ReportForm;
